import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, Trash2, RefreshCcw, Video, Globe, AlertCircle, Sparkles, CheckSquare, Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { ShareToggle } from "./ShareToggle";
import { MediaLightbox } from "./MediaLightbox";
import { ContentLikeButton } from "./ContentLikeButton";
import { OptimizedLikeButton } from "./OptimizedLikeButton";
import { useBatchLikeCounts } from "@/hooks/useBatchLikeCounts";
import { GallerySelectionBar } from "./GallerySelectionBar";
import { ClickablePrompt } from "./ClickablePrompt";
import { useGallerySelection } from "@/hooks/useGallerySelection";
import { ImageEditModal } from "./ImageEditModal";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";

// Model display name mapping
const IMAGE_MODEL_NAMES: Record<string, string> = {
  "flux-schnell": "Flux Schnell",
  "flux-dev": "Flux Dev",
  "nano-banana": "Nano Banana",
  "nano-banana-edit": "Nano Banana Edit",
  "google/gemini-2.5-flash-image-preview": "Nano Banana",
  "hidream-l1-fast": "HiDream L1 Fast",
};

interface Image {
  id: string;
  prompt: string;
  image_url: string | null;
  thumbnail_url: string | null;
  status: string;
  credits_used: number;
  created_at: string;
  settings: any;
  is_public?: boolean;
  error_message?: string | null;
  creator_username?: string | null;
}

interface ImageGalleryProps {
  user: any;
  refreshTrigger?: number;
  onMakeVideo?: (imageUrl: string, prompt: string) => void;
  onModelClick?: (model: string) => void;
  onRetry?: (prompt: string, settings: any) => void;
}

export const ImageGallery = ({ user, refreshTrigger, onMakeVideo, onModelClick, onRetry }: ImageGalleryProps) => {
  const [images, setImages] = useState<Image[]>([]);
  const [loading, setLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(12);
  const [lightboxImage, setLightboxImage] = useState<Image | null>(null);
  const [editingImage, setEditingImage] = useState<Image | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Batch like counts for public images (reduces N queries to 2)
  const publicImagesForLikes = useMemo(() => 
    !user ? images.slice(0, displayCount).filter(i => i.is_public && i.status === 'completed').map(i => ({ type: 'image' as const, id: i.id })) : [],
    [images, displayCount, user]
  );
  const { getLikeData, updateLikeData, userId: batchUserId } = useBatchLikeCounts(publicImagesForLikes);

  const fetchImages = async () => {
    try {
      if (user) {
        // Authenticated users see their own images
        const { data, error } = await supabase
          .from("images")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });

        if (error) throw error;
        setImages(data || []);
      } else {
        // Unauthenticated users see public images via secure view (no user_id exposed)
        const { data, error } = await supabase
          .from("public_images")
          .select("*")
          .order("created_at", { ascending: false });

        if (error) throw error;
        const publicImages = (data || []).map(img => ({ ...img, is_public: true })) as Image[];
        setImages(publicImages);
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      toast({
        title: "Error",
        description: "Failed to load images",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, [user, refreshTrigger]);

  // Set up realtime updates for image changes
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`image-updates-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'images',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Image update received:', payload);
          
          // Update specific image in state instead of refetching all
          if (payload.eventType === 'UPDATE' && payload.new) {
            setImages(prev => prev.map(image => 
              image.id === payload.new.id ? { ...image, ...payload.new } as Image : image
            ));
          } else if (payload.eventType === 'INSERT' && payload.new) {
            // Only add if it belongs to this user
            if (payload.new.user_id === user.id) {
              setImages(prev => [payload.new as Image, ...prev]);
            }
          } else if (payload.eventType === 'DELETE' && payload.old) {
            setImages(prev => prev.filter(image => image.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleDownload = async (imageUrl: string, prompt: string) => {
    try {
      toast({
        title: "Downloading...",
        description: "Please wait..."
      });

      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error('Failed to fetch image');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_').toLowerCase()}.webp`;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast({
        title: "Downloaded",
        description: "Image downloaded successfully",
      });
    } catch (error) {
      console.error("Download error:", error);
      // Fallback: open in new tab for iOS Safari
      window.open(imageUrl, '_blank');
      toast({
        title: "Download",
        description: "If download didn't start, long-press the image to save it",
      });
    }
  };

  const performDelete = async (imageId: string) => {
    try {
      const { error } = await supabase
        .from("images")
        .delete()
        .eq("id", imageId);

      if (error) throw error;

      setImages(images.filter(img => img.id !== imageId));
      toast({
        title: "Deleted",
        description: "Image deleted successfully",
      });
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Error",
        description: "Failed to delete image",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDelete = (imageId: string) => {
    setDeleteConfirmId(imageId);
  };

  const confirmSingleDelete = async () => {
    if (!deleteConfirmId) return;
    setIsDeleting(true);
    try {
      await performDelete(deleteConfirmId);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  // Gallery selection hook
  const {
    selectedItems,
    selectionMode,
    isBulkDeleting,
    isBulkDownloading,
    toggleSelection,
    selectAll,
    deselectAll,
    cancelSelection,
    startSelection,
    bulkDelete,
    bulkDownload,
    showBulkDeleteConfirm,
    setShowBulkDeleteConfirm,
    confirmBulkDelete,
    selectedCount,
  } = useGallerySelection({
    items: images.filter(i => i.status === 'completed' && i.image_url),
    type: 'image',
    onDelete: performDelete,
    onRefresh: () => setImages(prev => [...prev]),
  });

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="aspect-square bg-muted rounded-lg mb-3"></div>
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }


  if (images.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">
          {user ? "No images generated yet" : "No public images available"}
        </p>
        {user && <p className="text-sm text-muted-foreground">Create your first AI image above!</p>}
      </div>
    );
  }

  const displayedImages = images.slice(0, displayCount);
  const hasMore = images.length > displayCount;

  return (
    <div>
      {/* Selection header */}
      {user && !selectionMode && images.some(i => i.status === 'completed') && (
        <div className="flex justify-end mb-4">
          <Button variant="outline" size="sm" onClick={startSelection}>
            <CheckSquare className="h-4 w-4 mr-2" />
            Select
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayedImages.map((image) => (
        <Card key={image.id} className={`group overflow-hidden relative ${selectionMode && selectedItems.has(image.id) ? 'ring-2 ring-primary' : ''}`}>
          <CardContent className="p-0">
            {/* Selection checkbox */}
            {selectionMode && image.status === 'completed' && image.image_url && (
              <div 
                className="absolute top-2 left-2 z-30"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSelection(image.id);
                }}
              >
                <Checkbox
                  checked={selectedItems.has(image.id)}
                  className="h-5 w-5 bg-white/90 border-2"
                />
              </div>
            )}

            <div className="relative aspect-square">
              {image.status === 'completed' && image.image_url ? (
                <img
                  src={image.thumbnail_url || image.image_url}
                  alt={image.prompt || 'Generated image'}
                  className="w-full h-full object-cover cursor-pointer"
                  loading="lazy"
                  onClick={() => setLightboxImage(image)}
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  {image.status === 'pending' && (
                    <div className="text-center">
                      <RefreshCcw className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mb-2">Generating...</p>
                      {user && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(image.id)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  )}
                  {image.status === 'failed' && (
                    <div className="text-center p-4">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 text-destructive" />
                      <p className="text-sm font-medium text-destructive mb-1">Generation Failed</p>
                      {image.error_message && (
                        <p className="text-xs text-muted-foreground mb-2 line-clamp-3 max-w-[200px] mx-auto">
                          {image.error_message}
                        </p>
                      )}
                      <p className="text-xs text-green-600 mb-3">Credits refunded</p>
                      {user && (
                        <div className="flex gap-2 justify-center">
                          {onRetry && (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => {
                                onRetry(image.prompt, image.settings);
                                handleDelete(image.id);
                              }}
                            >
                              <RefreshCcw className="h-4 w-4 mr-1" />
                              Retry
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(image.id)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              

              {image.status === 'completed' && image.image_url && (
                <div className="absolute top-2 right-2 z-20">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      if (user && onMakeVideo) {
                        onMakeVideo(image.image_url!, image.prompt)
                      } else {
                        toast({
                          title: t("auth.loginRequired"),
                          description: "Please sign in to create videos from images",
                          variant: "default",
                        });
                      }
                    }}
                    className="bg-white text-black hover:bg-white/90 rounded-full px-3 py-1 text-xs font-medium shadow-md"
                  >
                    <Video className="h-3 w-3 mr-1" />
                    {t("main.makeVideo")}
                  </Button>
                </div>
              )}

              {image.status === 'completed' && image.image_url && (
                <div 
                  className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2"
                  onClick={() => setLightboxImage(image)}
                >
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(image.image_url!, image.prompt);
                    }}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {user && (
                    <>
                      <div onClick={(e) => e.stopPropagation()}>
                        <ShareToggle
                          id={image.id}
                          isPublic={image.is_public || false}
                          type="image"
                          prompt={image.prompt}
                          onToggle={(newState) => {
                            setImages(images.map(img => 
                              img.id === image.id ? { ...img, is_public: newState } : img
                            ))
                          }}
                        />
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingImage(image);
                        }}
                        title="Edit image"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(image.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
            
            <div className="p-4">
              {/* Badge row - consistent with VideoGallery */}
              <div className="flex gap-1 flex-wrap mb-2">
                {image.status !== 'completed' && (
                  <Badge variant="secondary" className="text-xs">
                    {image.status}
                  </Badge>
                )}
                {image.is_public && (
                  <Badge variant="outline" className="text-xs bg-green-500/20 text-green-500 border-green-500/50">
                    <Globe className="h-3 w-3 mr-1" />
                    Public
                  </Badge>
                )}
                {image.settings?.model && (
                  <Badge
                    variant="outline"
                    className={`text-xs bg-purple-500/20 text-purple-300 border-purple-500/50 ${onModelClick ? 'cursor-pointer hover:bg-purple-500/30 transition-colors' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onModelClick) onModelClick(image.settings.model);
                    }}
                    title={onModelClick ? "Try this model" : undefined}
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    {IMAGE_MODEL_NAMES[image.settings.model] || image.settings.model}
                  </Badge>
                )}
              </div>
              <ClickablePrompt 
                text={image.prompt} 
                tab="image"
                className="text-sm font-medium line-clamp-2 mb-2 pr-4"
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                {image.is_public ? (
                  user ? (
                    <ContentLikeButton
                      contentType="image"
                      contentId={image.id}
                      className="h-6"
                    />
                  ) : (
                    <OptimizedLikeButton
                      contentType="image"
                      contentId={image.id}
                      initialLikeCount={getLikeData('image', image.id).likeCount}
                      initialIsLiked={getLikeData('image', image.id).isLiked}
                      userId={batchUserId}
                      onLikeChange={(isLiked, delta) => updateLikeData('image', image.id, isLiked, delta)}
                      className="h-6"
                    />
                  )
                ) : (
                  <span></span>
                )}
                <span>
                  {image.is_public && image.creator_username && (
                    <span className="font-medium text-foreground">{image.creator_username} • </span>
                  )}
                  {new Date(image.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </CardContent>
          </Card>
        ))}
      </div>
      
      {hasMore && (
        <div className="flex justify-center mt-6">
          <Button 
            onClick={() => setDisplayCount(prev => prev + 12)}
            variant="outline"
          >
            Show More
          </Button>
        </div>
      )}

      {/* Lightbox for full-size image viewing */}
      {lightboxImage && lightboxImage.image_url && (
        <MediaLightbox
          type="image"
          url={lightboxImage.image_url}
          prompt={lightboxImage.prompt}
          open={!!lightboxImage}
          onOpenChange={(open) => !open && setLightboxImage(null)}
        />
      )}

      {/* Image Edit Modal */}
      {user && (
        <ImageEditModal
          image={editingImage}
          open={!!editingImage}
          onOpenChange={(open) => !open && setEditingImage(null)}
          onEditComplete={() => {
            setEditingImage(null);
            // Trigger refresh
            fetchImages();
          }}
          userId={user.id}
        />
      )}

      {/* Bulk action bar */}
      {selectionMode && (
        <GallerySelectionBar
          selectedCount={selectedItems.size}
          totalCount={images.filter(i => i.status === 'completed' && i.image_url).length}
          onSelectAll={selectAll}
          onDeselectAll={deselectAll}
          onCancel={cancelSelection}
          onDelete={bulkDelete}
          onDownload={bulkDownload}
          isDeleting={isBulkDeleting}
          isDownloading={isBulkDownloading}
        />
      )}

      {/* Single delete confirmation dialog */}
      <DeleteConfirmDialog
        open={!!deleteConfirmId}
        onOpenChange={(open) => !open && setDeleteConfirmId(null)}
        onConfirm={confirmSingleDelete}
        title="Delete this image?"
        description="This action cannot be undone."
        isDeleting={isDeleting}
      />

      {/* Bulk delete confirmation dialog */}
      <DeleteConfirmDialog
        open={showBulkDeleteConfirm}
        onOpenChange={setShowBulkDeleteConfirm}
        onConfirm={confirmBulkDelete}
        title={`Delete ${selectedCount} image${selectedCount > 1 ? 's' : ''}?`}
        description="This action cannot be undone."
        isDeleting={isBulkDeleting}
      />
    </div>
  );
};